import { AuthService } from "@/services/auth.service";
import { ShopWindowsService } from "@/services/shopWindow.service";
import { KeychainHandler } from "@/plugins/keychain/plugin";
import { Capacitor } from "@capacitor/core";

export default {
  namespaced: true,

  state: () => ({
    authenticated: false,
    // True durante o bootstrap do main.js (await reAuthenticate em flight).
    // Componente da rota '/' (Login.vue) usa isso pra esconder o form enquanto
    // o redirect pra my-courses ainda pode acontecer — evita flash visual da
    // tela de login pra user já autenticado. Vira false ao fim do main.js,
    // independente do resultado do auth (autenticado ou não).
    initializing: true,
    // STORY-1642: o redirect do boot para /p/my-courses (main.js, tryGo) ainda
    // está tentando. Separado de `initializing`, que a rede de segurança de 5 s
    // libera com o redirect ainda em voo: o toque num push esperava só o
    // initializing, navegava para a aula, e o tryGo seguinte o levava de volta
    // para Meus Cursos.
    bootRedirectPending: false,
  }),

  mutations: {
    SET_AUTHENTICATED(state, value) {
      state.authenticated = value;
    },
    SET_INITIALIZING(state, value) {
      state.initializing = value;
    },
    SET_BOOT_REDIRECT_PENDING(state, value) {
      state.bootRedirectPending = Boolean(value);
    },
  },

  actions: {
    async init({ commit }, { refreshToken, shopWindowId } = {}) {
      if (refreshToken) {
        AuthService.setRefreshToken(refreshToken);
        await AuthService.refreshTokens();

        if (ShopWindowsService.isValidShopWindowId(shopWindowId)) {
          ShopWindowsService.setCurrentShopWindow({ id: shopWindowId });
        }

        commit("SET_AUTHENTICATED", AuthService.hasAccessToken());
        return;
      }

      await this.dispatch("auth/reAuthenticate");
    },

    async refreshTokensOnReconnect() {
      if (!Capacitor.isNativePlatform()) return;
      if (!AuthService.hasRefreshToken()) return;
      try {
        // suppressRedirect igual ao interceptor (http.init.js). Ao voltar a
        // rede o access token (5min) está sempre vencido, então keep-alive,
        // loop de notificação, sync do push e o GET do sino disparam 401 no
        // mesmo instante; o refreshTokens é single-flight para todos (o backend
        // rotaciona o refresh token), e esta chamada só entra na mesma
        // renovação. Sem a flag, um 401 aqui faria _resetAuthData +
        // clearToLogin e o aluno seria deslogado, perdendo junto o
        // ead_cached_user, que é o que sustenta o acesso offline ao conteúdo já
        // baixado.
        //
        // Com a flag, a falha só propaga (o catch abaixo engole) e a próxima
        // request cai no interceptor, que tem o relogin biométrico. A
        // recuperação fica com quem já sabe fazê-la.
        await AuthService.refreshTokens({ suppressRedirect: true });
      } catch (e) {}
    },

    async reAuthenticate({ commit }) {
      if (!Capacitor.isNativePlatform()) return;

      // Tenta autenticar via cache sem rede. Retorna true se conseguiu.
      const tryOfflineCache = () => {
        if (!AuthService.hasRefreshToken()) return false;
        try {
          const cached = JSON.parse(localStorage.getItem('ead_cached_user') || 'null');
          if (!cached?.studentId) return false;
          commit("SET_AUTHENTICATED", true);
          // navigator.onLine é não-confiável no WebView; popular o user store evita redirect para login no checkAccessMiddleware
          commit("user/SET_CURRENT_USER", cached, { root: true });
          return true;
        } catch { return false; }
      };

      // Offline: entra direto pelo cache
      if (!this.state.dom.isConnected) {
        tryOfflineCache();
        return;
      }

      if (AuthService.hasRefreshToken()) {
        try {
          await AuthService.refreshTokens();
          commit("SET_AUTHENTICATED", true);
          return;
        } catch (e) {
          // Erro de rede (sem resposta HTTP, status === false): navigator.onLine pode estar
          // desatualizado no WebView ao abrir o app — usa cache offline como fallback.
          if (e.status === false && tryOfflineCache()) return;
        }
      }

      // refresh falhou ou não existe — tenta biometria
      try {
        const credentials = await KeychainHandler.getCredentials();
        if (credentials) {
          await AuthService.makeLogin(credentials);
          commit("SET_AUTHENTICATED", true);
        }
      } catch (e) {}
    },
  },

  getters: {
    isAuthenticated: (state) => state.authenticated || AuthService.hasAccessToken(),
  },
};
